import { useState, useEffect, useRef, useCallback } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./Messaging.css";
import {
  PaperAirplaneIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  SparklesIcon,
  DocumentTextIcon,
  ClockIcon,
  UserGroupIcon,
  ClipboardDocumentIcon,
  AcademicCapIcon,
  CalendarDaysIcon,
  PaperClipIcon,
  DocumentIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { useStudioUser } from "../hooks/useStudioUser";
import Notification from "../components/Notification";
import Tooltip from "../components/Tooltip";
import { getLocalDateString } from "../utils/timezone";
import { Link, useLocation } from "react-router-dom";
import PlanGatedPage from "../components/PlanGatedPage";
import { useStudioPlan } from "../hooks/useStudioPlan";
import { isPaymentLinkTag } from "../plans/tagFilters";

const modules = {
  toolbar: {
    container: "#custom-toolbar",
  },
};

const formats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "list",
  "bullet",
  "align",
  "link",
  "image",
];

interface StudioUser {
  id: string;
  studio_id: string | null;
}

interface EmailTag {
  id: string;
  tag_name: string;
  tag_key: string;
  value?: string;
}

interface Student {
  id: string;
  name: string;
  email: string;
  belt?: string;
  [key: string]: any;
}

interface Lead {
  id: string;
  name: string;
  email: string;
  interest?: string;
  [key: string]: any;
}

interface Template {
  id: string;
  name: string;
  template: string;
}

interface Recipient {
  id: string;
  name: string;
  email: string;
  type: "student" | "lead" | "staff";
  studentId?: string;
  primaryEmail?: string;
  emailKind?: "primary" | "alternative";
  [key: string]: any;
}

const STUDENT_ALT_EMAIL_RECIPIENT_SUFFIX = "::alt";

function getStudentAltRecipientId(studentId: string): string {
  return `${studentId}${STUDENT_ALT_EMAIL_RECIPIENT_SUFFIX}`;
}

function formatRecipientTypeLabel(recipient: Recipient): string {
  const base =
    recipient.type === "student"
      ? "Student"
      : recipient.type === "lead"
      ? "Lead"
      : "Staff";
  if (recipient.type === "student" && recipient.emailKind === "alternative") {
    return `${base} · Alternative`;
  }
  if (recipient.type === "student" && recipient.emailKind === "primary") {
    return `${base} · Primary`;
  }
  return base;
}

function buildRecipientTagData(
  studio: Studio | null,
  recipient: Recipient
): Record<string, string> {
  const isStudent = recipient.type === "student";
  const isLead = recipient.type === "lead";
  const isStaff = recipient.type === "staff";

  return {
    studioName: studio?.name || "",
    studioAddress: studio?.address || "",
    studioPhone: studio?.phone || "",
    studioEmail: studio?.email || "",
    studentName: isStudent ? recipient.name || "" : "",
    studentEmail: isStudent
      ? recipient.primaryEmail || recipient.email || ""
      : "",
    alternativeEmail:
      isStudent && recipient.emailKind === "alternative"
        ? recipient.email || ""
        : "",
    leadName: isLead ? recipient.name || "" : "",
    leadEmail: isLead ? recipient.email || "" : "",
    staffName: isStaff ? recipient.name || "" : "",
    staffEmail: isStaff ? recipient.email || "" : "",
    name: recipient.name || "",
    email: recipient.email || "",
    beltRank: isStudent ? recipient.belt || "" : "",
    studio_name: studio?.name || "",
    studio_address: studio?.address || "",
    studio_phone: studio?.phone || "",
    studio_email: studio?.email || "",
    student_name: isStudent ? recipient.name || "" : "",
    student_email: isStudent
      ? recipient.primaryEmail || recipient.email || ""
      : "",
    alternative_email:
      isStudent && recipient.emailKind === "alternative"
        ? recipient.email || ""
        : "",
    lead_name: isLead ? recipient.name || "" : "",
    lead_email: isLead ? recipient.email || "" : "",
    staff_name: isStaff ? recipient.name || "" : "",
    staff_email: isStaff ? recipient.email || "" : "",
    belt_rank: isStudent ? recipient.belt || "" : "",
    _recipient_type: recipient.type || "unknown",
    _recipient_id: recipient.studentId || recipient.id,
  };
}

interface Studio {
  id: string;
  name: string;
  address: string;
  phone: string;
  email: string;
}

interface Class {
  id: string;
  class_name: string;
  start_time: string;
  end_time: string;
  studio_id: string;
  days: string[];
  assigned_instructors?: any[];
}

interface Event {
  id: string;
  event_name: string;
  from_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  studio_id: string;
  type: string;
  assigned_instructors?: any[];
}

function buildCustomRecipientTagData(
  studio: Studio | null,
  email: string
): Record<string, string> {
  return {
    studioName: studio?.name || "",
    studioAddress: studio?.address || "",
    studioPhone: studio?.phone || "",
    studioEmail: studio?.email || "",
    studentName: "",
    studentEmail: "",
    leadName: "",
    leadEmail: "",
    staffName: "",
    staffEmail: "",
    name: "",
    email: email,
    beltRank: "",
    studio_name: studio?.name || "",
    studio_address: studio?.address || "",
    studio_phone: studio?.phone || "",
    studio_email: studio?.email || "",
    student_name: "",
    student_email: "",
    lead_name: "",
    lead_email: "",
    staff_name: "",
    staff_email: "",
    belt_rank: "",
    _recipient_type: "custom",
    _recipient_id: "",
  };
}

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  subject: 200,
  editorContent: 1000000, // 1MB limit for email content (more realistic for emails)
  templateName: 100,
  aiInput: 1000,
  searchTerm: 100,
  classSearchTerm: 100,
  eventSearchTerm: 100,
  templateSearchTerm: 100,
  scheduledDate: 10, // MM/DD/YYYY
  scheduledTime: 5,  // HH:MM
  customEmail: 254,
};

/** Match backend `/emails/send` validation */
const RECIPIENT_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions
const EMAIL_TEMPLATES_LIMIT_PAID = 10;

// Image compression settings
const IMAGE_COMPRESSION_QUALITY = 0.35; // 35% quality for images over 300KB
const IMAGE_MAX_WIDTH = 300; // Smaller width for better email compatibility and faster delivery
const IMAGE_MAX_FILE_SIZE = 300 * 1024; // 300KB - compress if larger than 300KB

// Gmail API size limits
const GMAIL_MAX_EMAIL_SIZE = 25 * 1024 * 1024; // 25MB Gmail limit
const SAFE_EMAIL_SIZE_LIMIT = 10 * 1024 * 1024; // 10MB safe limit to avoid issues

/** Shown when an image is attached so guidance stays aligned with the limits above. */
const EMAIL_IMAGE_ATTACHMENT_TIPS = {
  recommendedMaxWidthPx: 600,
  autoCompressAboveKb: Math.round(IMAGE_MAX_FILE_SIZE / 1024),
  autoCompressMaxWidthPx: IMAGE_MAX_WIDTH,
  safeTotalEmailMb: Math.round(SAFE_EMAIL_SIZE_LIMIT / (1024 * 1024)),
} as const;

function EmailImageAttachmentTips({
  attachments,
  expanded,
}: {
  attachments: Array<{ type: string; wasOptimized?: boolean }>;
  expanded: boolean;
}) {
  if (!expanded) {
    return null;
  }

  const anyOptimized = attachments
    .filter((file) => file.type.startsWith('image/'))
    .some((file) => file.wasOptimized);

  return (
    <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
      <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
        Recommended specs for email images
      </p>
      <ul className="mt-2 list-disc list-inside space-y-1 text-xs text-blue-700 dark:text-blue-300">
        <li>
          For the sharpest result, use JPEG or PNG around{' '}
          {EMAIL_IMAGE_ATTACHMENT_TIPS.recommendedMaxWidthPx}px wide and under{' '}
          {EMAIL_IMAGE_ATTACHMENT_TIPS.autoCompressAboveKb} KB before you attach.
        </li>
        <li>
          Images larger than {EMAIL_IMAGE_ATTACHMENT_TIPS.autoCompressAboveKb} KB are
          automatically resized to about {EMAIL_IMAGE_ATTACHMENT_TIPS.autoCompressMaxWidthPx}px
          wide and compressed, which can look softer than the original.
        </li>
        <li>
          Keep the whole email — message plus all attachments — under about{' '}
          {EMAIL_IMAGE_ATTACHMENT_TIPS.safeTotalEmailMb} MB so Gmail delivery stays reliable.
        </li>
        {anyOptimized && (
          <li>
            At least one attached image was optimized automatically for faster delivery.
          </li>
        )}
      </ul>
    </div>
  );
}

// Input sanitization function (matching PublicWaiver.tsx approach)
const sanitizeInput = (input: string, fieldType?: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // For email content and AI input, preserve formatting but remove dangerous content
  if (fieldType === 'editorContent' || fieldType === 'aiInput') {
    // Check if content has base64 images first
    const hasBase64Images = /data:image\/[^;]+;base64,/i.test(sanitized);
    

    
    if (hasBase64Images) {
      // For content with images, only remove the most critical threats
      // Avoid removing 'on\w+\s*=' patterns as base64 data can contain sequences that look like event handlers
      sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
      sanitized = sanitized.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');
      sanitized = sanitized.replace(/javascript:/gi, '');
      sanitized = sanitized.replace(/vbscript:/gi, '');
      // Don't remove data:text/html as it might be part of legitimate image data URLs
    } else {
      // For content without images, use full sanitization
      sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
      sanitized = sanitized.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');
      sanitized = sanitized.replace(/javascript:/gi, '');
      sanitized = sanitized.replace(/vbscript:/gi, '');
      sanitized = sanitized.replace(/on\w+\s*=/gi, '');
      // Don't remove data:text/html as it might be part of legitimate image data URLs
    }
    

    
    // Don't trim to preserve user intent
  } else if (fieldType === 'subject' || fieldType === 'templateName' || fieldType === 'searchTerm') {
    // For names and search terms, preserve spaces but remove dangerous content
    // For subjects, be more lenient - only remove actual HTML tags, not all angle brackets
    if (fieldType === 'subject') {
      sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, ''); // Remove script tags
      sanitized = sanitized.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, ''); // Remove iframe tags
      sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
      sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
      sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
      sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
    } else {
      sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags for other fields
      sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
      sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
      sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
      sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
    }
    // Don't trim for search terms - preserve user intent
  } else {
    // For other fields, use more aggressive sanitization
    sanitized = sanitized.trim();
    sanitized = sanitized.replace(/[<>]/g, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/data:text\/html/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  }
  
  return sanitized;
};

// Enhanced validation function (matching PublicWaiver.tsx approach)
const validateInput = (value: string, type: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  
  const sanitized = sanitizeInput(value, type);
  if (sanitized.length === 0) return false;

  // Check length limits
  if (MAX_LENGTHS[type] && sanitized.length > MAX_LENGTHS[type]) {
    return false;
  }
  
  // Type-specific validation
  switch (type) {
    case 'subject':
      // Check for dangerous patterns in subject, but allow spaces and normal punctuation
      const subjectDangerousPatterns = /<script\b|javascript:|vbscript:|on\w+\s*=|data:text\/html/i;
      return !subjectDangerousPatterns.test(sanitized) && sanitized.length >= 1;
    case 'editorContent':
      // Check for dangerous patterns in email content, but allow legitimate image data URLs and base64 content
      const hasBase64Images = /data:image\/[^;]+;base64,/i.test(sanitized);
      

      
      if (hasBase64Images) {
        // For content with images, only check for the most dangerous patterns
        // Avoid checking for 'on\w+\s*=' as base64 data can contain sequences that look like event handlers
        // Use separate checks instead of complex regex to avoid false positives
        const hasScript = /<script\b/i.test(sanitized);
        const hasJavaScript = /javascript:/i.test(sanitized);
        const hasVBScript = /vbscript:/i.test(sanitized);
        // Don't check for data:text/html as it might be part of legitimate image data URLs
        

        
        return !hasScript && !hasJavaScript && !hasVBScript && sanitized.length >= 1;
      } else {
        // For content without images, use full validation but exclude image data URLs
        // Use separate checks instead of complex regex to avoid false positives
        const hasScript = /<script\b/i.test(sanitized);
        const hasJavaScript = /javascript:/i.test(sanitized);
        const hasVBScript = /vbscript:/i.test(sanitized);
        const hasEventHandlers = /on\w+\s*=/i.test(sanitized);
        // Don't check for data:text/html as it might be part of legitimate image data URLs
        

        
        return !hasScript && !hasJavaScript && !hasVBScript && !hasEventHandlers && sanitized.length >= 1;
      }
    case 'templateName':
      // Template names should be alphanumeric, spaces, hyphens, underscores
      const templateNameRegex = /^[a-zA-Z0-9\s\-_]+$/;
      return templateNameRegex.test(sanitized) && sanitized.length >= 1;
    case 'aiInput':
      // Check for dangerous patterns in AI input
      const aiDangerousPatterns = /<script\b|javascript:|vbscript:|on\w+\s*=|data:text\/html/i;
      return !aiDangerousPatterns.test(sanitized) && sanitized.length >= 1;
    case 'scheduledDate':
      // Date should be in MM/DD/YYYY format
      const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
      return dateRegex.test(sanitized);
    case 'scheduledTime':
      // Time should be in H:MM or HH:MM format
      const timeRegex = /^\d{1,2}:\d{2}$/;
      return timeRegex.test(sanitized);

    default:
      return sanitized.length > 0;
  }
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`Messaging Security Event: ${event}`, {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      details
    });
  }
};

// Debug logger (opt-in only). Enable via:
//   localStorage.setItem('DEBUG_MESSAGING','1')  // and reload
// Or set window.__DEBUG_MESSAGING__ = true
const debugEnabled = (): boolean => {
  try {
    if (process.env.NODE_ENV === 'production') return false;
    // @ts-ignore
    if (typeof window !== 'undefined' && (window as any).__DEBUG_MESSAGING__ === true) return true;
    // @ts-ignore
    const flag = typeof window !== 'undefined' ? window.localStorage?.getItem('DEBUG_MESSAGING') : null;
    return flag === '1';
  } catch {
    return false;
  }
};
const debugLog = (...args: any[]) => {
  if (debugEnabled()) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
};

// Ultra-aggressive image compression utility with multi-pass compression
const compressImage = (file: File, quality: number = 0.2, maxWidth: number = 400): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;
      
      // More aggressive size reduction
      if (width > maxWidth || height > maxWidth) {
        const ratio = Math.min(maxWidth / width, maxWidth / height);
        width = Math.floor(width * ratio);
        height = Math.floor(height * ratio);
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress the image
      ctx?.drawImage(img, 0, 0, width, height);
      
      // Try multiple compression levels if needed
      const tryCompress = (currentQuality: number) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // If still too large and we can compress more, try again
              if (blob.size > 50 * 1024 && currentQuality > 0.1) { // 50KB limit
                tryCompress(currentQuality - 0.05); // Reduce quality by 5%
                return;
              }
              
              // Create a new file with compressed data
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          file.type,
          currentQuality
        );
      };
      
      tryCompress(quality);
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};

// Enhanced file upload handler for general files
const handleFileUpload = async (file: File, studioId?: string, authToken?: string): Promise<{url: string, size: number}> => {
  try {
    // Convert to base64 for upload
    const base64Data = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result);
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });

    // Upload file to server
    debugLog('📤 Uploading file to server...');
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/upload/file`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        fileData: base64Data,
        fileName: file.name,
        fileType: file.type,
        studioId: studioId || null
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to upload file');
    }

    const uploadResult = await response.json();
    debugLog('✅ File uploaded successfully:', uploadResult.fileUrl);
    
    // Return the file URL and actual file size
    return {
      url: uploadResult.fileUrl,
      size: uploadResult.size || file.size
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('File upload failed:', error);
    throw new Error('Failed to upload file. Please try again.');
  }
};

// Enhanced image handler for Quill editor - now uploads to server instead of embedding
const handleImageUpload = async (file: File, studioId?: string, authToken?: string): Promise<{url: string, size: number}> => {
  try {
    // Only compress if file is larger than threshold
    let fileToUse = file;
    let wasCompressed = false;
    let originalSize = file.size;
    let compressedSize = file.size;
    
    if (file.size > IMAGE_MAX_FILE_SIZE) {
      // Only compress images larger than 300KB
      fileToUse = await compressImage(file, IMAGE_COMPRESSION_QUALITY, IMAGE_MAX_WIDTH);
      wasCompressed = true;
      compressedSize = fileToUse.size;
      
      // Calculate compression ratio
      const compressionRatio = ((originalSize - compressedSize) / originalSize * 100).toFixed(1);
      debugLog(`📸 Image compressed: ${(originalSize / 1024 / 1024).toFixed(1)}MB → ${(compressedSize / 1024 / 1024).toFixed(1)}MB (${compressionRatio}% smaller)`);
    }
    
    // Convert to base64 for upload
    const base64Data = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result);
      };
      reader.onerror = () => reject(new Error('Failed to read image'));
      reader.readAsDataURL(fileToUse);
    });

        // Upload image to server
    debugLog('📤 Uploading image to server...');
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/upload/image`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        imageData: base64Data,
        fileName: file.name,
        studioId: studioId || null
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to upload image');
    }

    const uploadResult = await response.json();
    debugLog('✅ Image uploaded successfully:', uploadResult.imageUrl);
    
    // Return the image URL and actual file size
    return {
      url: uploadResult.imageUrl,
      size: uploadResult.size || compressedSize
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Image upload failed:', error);
    throw new Error('Failed to upload image. Please try again.');
  }
};

// Custom toolbar component
function CustomToolbar({
  imageSize,
  onImageSizeChange,
  showImageControls,
}: {
  imageSize: { width: string; height: string };
  onImageSizeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showImageControls: boolean;
}) {
  return (
    <div id="custom-toolbar">
      <select
        className="ql-header"
        defaultValue=""
        onChange={(e) => e.persist()}
      >
        <option value="1" />
        <option value="2" />
        <option value="3" />
        <option value="" />
      </select>
      <button className="ql-bold" />
      <button className="ql-italic" />
      <button className="ql-underline" />
      <button className="ql-strike" />
      <button className="ql-list" value="ordered" />
      <button className="ql-list" value="bullet" />
      <button className="ql-align" value="" />
      <button className="ql-align" value="center" />
      <button className="ql-align" value="right" />
      <button className="ql-link" />
      {showImageControls && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            marginLeft: 8,
          }}
        >
          <label style={{ fontSize: 12 }}>
            W:
            <input
              type="number"
              name="width"
              value={imageSize.width}
              onChange={onImageSizeChange}
              style={{ width: 48, marginLeft: 2, fontSize: 12 }}
            />
          </label>
        </span>
      )}
    </div>
  );
}

// Utility function to replace template tags
function replaceTemplateTags(template: string, data: Record<string, string>) {
  return template.replace(/{{(.*?)}}/g, (_, tag) => data[tag.trim()] || "");
}

// Utility function for consistent input styling
const getInputClassName = (baseClasses: string) => {
  return `${baseClasses} bg-white dark:bg-gray-700 text-gray-900 dark:text-white`;
};

// Date formatter function
const formatDate = (value: string): string => {
  // Remove all non-digits
  const numbers = value.replace(/\D/g, '');
  
  // Limit to 8 digits (MMDDYYYY)
  const limited = numbers.slice(0, 8);
  
  // Format as MM/DD/YYYY
  if (limited.length >= 5) {
    return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
  } else if (limited.length >= 3) {
    return `${limited.slice(0, 2)}/${limited.slice(2)}`;
  } else if (limited.length >= 1) {
    return limited;
  }
  
  return '';
};

// Time formatter function (without AM/PM handling)
const formatTime = (value: string): string => {
  // Remove all non-alphanumeric characters except colon
  const cleaned = value.replace(/[^0-9:]/gi, '');
  
  // Extract numbers
  const numbers = cleaned.replace(/[^0-9]/g, '');
  
  // Limit to 4 digits (HHMM)
  const limited = numbers.slice(0, 4);
  
  // Format as HH:MM
  if (limited.length >= 3) {
    const hours = limited.slice(0, 2);
    const minutes = limited.slice(2, 4);
    const hourNum = parseInt(hours);
    
    // Only convert 24-hour to 12-hour if it's clearly 24-hour format
    if (hourNum > 12 && hourNum <= 23) {
      const hour12 = hourNum - 12;
      return `${hour12.toString().padStart(2, '0')}:${minutes}`;
    } else if (hourNum === 0) {
      return `12:${minutes}`;
    } else {
      return `${hours}:${minutes}`;
    }
  } else if (limited.length >= 1) {
    return limited;
  }
  
  return '';
};

// Convert MM/DD/YYYY to YYYY-MM-DD for backend
const convertDateToISO = (dateString: string): string => {
  if (!dateString || dateString.length !== 10) return '';
  
  const parts = dateString.split('/');
  if (parts.length !== 3) return '';
  
  const month = parts[0];
  const day = parts[1];
  const year = parts[2];
  
  return `${year}-${month}-${day}`;
};

// Convert military time to 12-hour format
const formatTime12Hour = (timeString: string): string => {
  if (!timeString) return '';
  
  try {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const minute = parseInt(minutes);
    
    if (isNaN(hour) || isNaN(minute)) return timeString;
    
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    
    return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch (error) {
    return timeString;
  }
};

// Convert local time to UTC for database storage
const convertToUTC = (date: string, time: string, ampm: string, timezone: string): string => {
  try {
    debugLog('[convertToUTC] Input:', { date, time, ampm, timezone });
    
    // Parse the MM/DD/YYYY date format
    const [month, day, year] = date.split('/');
    debugLog('[convertToUTC] Parsed date parts:', { month, day, year });
    
    // Convert to 24-hour format
    let [hours, minutes] = time.split(':');
    let hour24 = parseInt(hours);
    
    if (ampm === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (ampm === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
    
    debugLog('[convertToUTC] 24-hour format:', { hour24, minutes });
    
    // SIMPLIFIED APPROACH: Use the browser's current timezone offset as a baseline
    // and adjust based on the studio timezone
    
    // Create a date in the browser's local timezone first
    const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), hour24, parseInt(minutes), 0);
    debugLog('[convertToUTC] Local date in browser timezone:', localDate.toString());
    debugLog('[convertToUTC] Local date ISO (browser interprets as local):', localDate.toISOString());
    
    // Get current time for comparison
    const now = new Date();
    debugLog('[convertToUTC] Current browser time:', now.toString());
    debugLog('[convertToUTC] Current UTC time:', now.toISOString());
    
    // For now, let's assume the user's browser is in the same timezone as the studio
    // This is a temporary fix - in production you'd want proper timezone conversion
    debugLog('[convertToUTC] Using browser timezone assumption for studio timezone');
    
    // Since the Date constructor creates dates in local time and toISOString() converts to UTC,
    // the localDate.toISOString() should give us the correct UTC time
    const utcDate = localDate;
    debugLog('[convertToUTC] Using date as-is (browser handles UTC conversion):', utcDate.toString());
    
    // Ensure the scheduled time is in the future
    if (utcDate <= now) {
      debugLog('[convertToUTC] WARNING: Scheduled time is in the past, adding 2 minutes buffer...');
      utcDate.setMinutes(utcDate.getMinutes() + 2);
      debugLog('[convertToUTC] Adjusted time:', utcDate.toString());
    }
    
    const utcString = utcDate.toISOString();
    debugLog('[convertToUTC] Final UTC string:', utcString);
    
    return utcString;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error converting time to UTC:', error);
    throw new Error('Invalid date or time format');
  }
};

export default function Messaging() {
  const { studioUser } = useStudioUser();
  const { session, getAuthToken } = useAuth();
  const { hasCapability, plan } = useStudioPlan();
  const location = useLocation();
  const [studio, setStudio] = useState<Studio | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [selectedRecipientEmails, setSelectedRecipientEmails] = useState<
    string[]
  >([]);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<
    string[]
  >([]);
  const [editorContent, setEditorContent] = useState("");
  const [emailTags, setEmailTags] = useState<EmailTag[]>([]);
  const [subject, setSubject] = useState("");
  const [isRecipientModalOpen, setIsRecipientModalOpen] = useState(false);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [aiInput, setAIInput] = useState("");
  const [aiOutput, setAIOutput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [customEmailInput, setCustomEmailInput] = useState("");
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateSearchTerm, setTemplateSearchTerm] = useState("");
  const [loadingRecipients, setLoadingRecipients] = useState(true);
  const [typeFilter, setTypeFilter] = useState<
    "all" | "student" | "lead" | "staff" | "alternative"
  >("all");
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(
    null
  );
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    title?: string;
  }>({
    show: false,
    message: "",
  });

  // Security-related state
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);

  // Store pending birthday emails to process when recipients load
  const pendingBirthdayEmailsRef = useRef<string[] | null>(null);
  const pendingBirthdayStudentsRef = useRef<Array<{ name: string; email: string; birthday: string }> | null>(null);

  // Scheduling state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [scheduledAMPM, setScheduledAMPM] = useState("AM");
  const [isScheduling, setIsScheduling] = useState(false);

  // Template limits (2 on free, 10 on paid)
  const emailTemplatesLimit =
    plan?.capabilities.maxEmailTemplates ?? EMAIL_TEMPLATES_LIMIT_PAID;
  const emailTemplatesLimitReached = templates.length >= emailTemplatesLimit;
  const [studioTimezone, setStudioTimezone] = useState("America/New_York");

  // Gmail connection state
  const [isGmailConnected, setIsGmailConnected] = useState(false);
  const [gmailCheckLoading, setGmailCheckLoading] = useState(true);
  const [gmailCheckComplete, setGmailCheckComplete] = useState(false);

  // Image selection and size state
  const [selectedImage, setSelectedImage] = useState<HTMLImageElement | null>(
    null
  );
  const [imageSize, setImageSize] = useState<{
    width: string;
    height: string;
  }>({ width: "", height: "" });

  const [isGenerating, setIsGenerating] = useState(false);

  const [isCompressingImage, setIsCompressingImage] = useState(false);
  const [showEmailImageSpecs, setShowEmailImageSpecs] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // Class filtering state
  const [classes, setClasses] = useState<Class[]>([]);
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [showClassModal, setShowClassModal] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [classSearchTerm, setClassSearchTerm] = useState("");

  // Event filtering state
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [eventSearchTerm, setEventSearchTerm] = useState("");

  const quillRef = useRef<ReactQuill>(null);

  useEffect(() => {
    if (studioUser?.studio_id) {
      fetchStudioData();
      checkGmailStatus();
      loadEmailTags();
      loadTemplates();
      loadRecipients();
      loadClasses();
      loadEvents();
      fetchCsrfToken();
    }
  }, [studioUser?.studio_id]);

  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setCsrfToken(data.token);
        } else {
          logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
        }
      } else {
        logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
      }
    } catch (error) {
      logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
      // In development, generate a fallback token
      if (process.env.NODE_ENV !== 'production') {
        const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
        setCsrfToken(fallbackToken);
        debugLog('Using fallback CSRF token in development mode');
      }
    }
  };

  // Handle birthday emails from navigation state
  useEffect(() => {
    const state = location.state as { birthdayEmails?: string[]; birthdayStudents?: Array<{ name: string; email: string; birthday: string }> } | null;
    
    // Store birthday emails and students from navigation state
    if (state?.birthdayEmails && Array.isArray(state.birthdayEmails)) {
      pendingBirthdayEmailsRef.current = state.birthdayEmails;
    }
    if (state?.birthdayStudents && Array.isArray(state.birthdayStudents)) {
      pendingBirthdayStudentsRef.current = state.birthdayStudents;
    }
    
    // Clear the navigation state immediately to prevent re-processing
    if (state?.birthdayEmails || state?.birthdayStudents) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  // Process pending birthday emails when recipients are loaded
  useEffect(() => {
    if (pendingBirthdayEmailsRef.current && !loadingRecipients && recipients.length > 0) {
      const birthdayEmails = pendingBirthdayEmailsRef.current;
      const birthdayStudents = pendingBirthdayStudentsRef.current;
      
      // Set the emails
      setSelectedRecipientEmails(birthdayEmails);
      
      // Match emails with recipients, but only if they match the birthday students by name and email
      // This ensures we only get the actual birthday students, not other recipients with the same email
      const selectedIds = recipients
        .filter(r => {
          // Check if this recipient's email is in the birthday emails
          if (!birthdayEmails.includes(r.email)) return false;
          
          // If we have birthday students data, also verify the name matches
          if (birthdayStudents && birthdayStudents.length > 0) {
            const matchingStudent = birthdayStudents.find(
              bs => bs.email === r.email && bs.name === r.name
            );
            return !!matchingStudent;
          }
          
          // If no birthday students data, just match by email (fallback)
          return true;
        })
        .map(r => r.id);
      setSelectedRecipientIds(selectedIds);
      
      // Set a default subject for birthday messages with studio name
      const studioName = studio?.name || "Your Studio";
      setSubject(`Happy Birthday from ${studioName}!`);
      
      // Clear the pending emails after processing
      pendingBirthdayEmailsRef.current = null;
      pendingBirthdayStudentsRef.current = null;
    } else if (pendingBirthdayEmailsRef.current && !loadingRecipients) {
      // Even if recipients are empty, set the emails (they'll be used when sending)
      setSelectedRecipientEmails(pendingBirthdayEmailsRef.current);
      const studioName = studio?.name || "Your Studio";
      setSubject(`Happy Birthday from ${studioName}!`);
      pendingBirthdayEmailsRef.current = null;
      pendingBirthdayStudentsRef.current = null;
    }
  }, [recipients, loadingRecipients, studio?.name]);

  const fetchStudioData = async () => {
    if (!studioUser?.studio_id) return;
    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/${studioUser.studio_id}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch studio data");
      }
      const data = await response.json();
      setStudio(data);
      setStudioTimezone(data.timezone || "America/New_York");
    } catch (error) {
      // Error handling without console.error
    }
  };

  const checkGmailStatus = async () => {
    try {
      const authToken = getAuthToken();
      if (!authToken) {
        setGmailCheckLoading(false);
        setGmailCheckComplete(true);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/gmail/status`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setIsGmailConnected(data.isConfigured);
        
        // Don't show notification automatically - let users explore the interface first
      } else {
        setIsGmailConnected(false);
        setNotification({
          show: true,
          title: "Connection Check Failed",
          message: "Unable to verify Gmail connection. Please check your internet connection.",
        });
      }
    } catch (error) {
      setIsGmailConnected(false);
      setNotification({
        show: true,
        title: "Connection Error",
        message: "Failed to check Gmail connection. Please try refreshing the page.",
      });
    } finally {
      setGmailCheckLoading(false);
      setGmailCheckComplete(true);
    }
  };

  const loadEmailTags = async () => {
    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/email-tags`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch email tags");
      }
      const data = await response.json();
      setEmailTags(data);
    } catch (error) {
      // Error handling without console.error
    }
  };

  const loadClasses = async () => {
    if (!studioUser?.studio_id) return;

    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/classes/${studioUser.studio_id}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch classes");
      }

      const data = await response.json();
      setClasses(data);
    } catch (error) {
      // Error handling without console.error
    } finally {
      setLoadingClasses(false);
    }
  };

  const loadEvents = async () => {
    if (!studioUser?.studio_id) return;

    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/events/${studioUser.studio_id}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch events");
      }

      const data = await response.json();
      setEvents(data);
    } catch (error) {
      // Error handling without console.error
    } finally {
      setLoadingEvents(false);
    }
  };

  useEffect(() => {
    loadEmailTags();
  }, []);

  const loadTemplates = async () => {
    if (!studioUser?.studio_id) return;

    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/email-templates/${studioUser.studio_id}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch email templates");
      }

      const data = await response.json();
      setTemplates(data);
    } catch (error) {
      // Error handling without console.error
    }
  };

  const loadRecipients = async () => {
    if (!studioUser?.studio_id) return;

    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      // Fetch all data in parallel with pagination to get all records
      const [studentsResponse, leadsResponse, staffResponse] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/${studioUser.studio_id}?limit=10000`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        ),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/leads/${studioUser.studio_id}?limit=10000`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        ),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/staff/${studioUser.studio_id}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        ),
      ]);

      // Check if all responses are successful
      if (!studentsResponse.ok || !leadsResponse.ok || !staffResponse.ok) {
        throw new Error(`API request failed: Students: ${studentsResponse.status}, Leads: ${leadsResponse.status}, Staff: ${staffResponse.status}`);
      }

      const [studentsData, leadsData, staffData] = await Promise.all([
        studentsResponse.json(),
        leadsResponse.json(),
        staffResponse.json(),
      ]);

      // Combine and format recipients with proper error handling
      const formattedRecipients = [
        // Students — primary email and optional alternative as separate rows
        ...(studentsData.data || []).flatMap((student: any) => {
          if (!student) return [];
          const rows: Recipient[] = [];
          const primaryEmail = String(student.email || "").trim();
          const alternativeEmail = String(student.alternative_email || "").trim();

          if (primaryEmail) {
            rows.push({
              id: student.id,
              studentId: student.id,
              name: student.name,
              email: primaryEmail,
              primaryEmail,
              emailKind: "primary",
              belt: student.belt || "",
              type: "student",
            });
          }

          if (
            alternativeEmail &&
            alternativeEmail.toLowerCase() !== primaryEmail.toLowerCase()
          ) {
            rows.push({
              id: getStudentAltRecipientId(student.id),
              studentId: student.id,
              name: student.name,
              email: alternativeEmail,
              primaryEmail: primaryEmail || undefined,
              emailKind: "alternative",
              belt: student.belt || "",
              type: "student",
            });
          }

          return rows;
        }),
        // Leads - filter out those without email addresses
        ...(leadsData.data || [])
          .filter((lead: any) => lead && lead.email)
          .map((lead: any) => ({
            id: lead.id,
            name: lead.name,
            email: lead.email || "",
            type: "lead" as const,
          })),
        // Staff - filter out those without email addresses
        ...(staffData || [])
          .filter((staff: any) => staff && staff.email)
          .map((staff: any) => ({
            id: staff.id,
            name: staff.name,
            email: staff.email || "",
            type: "staff" as const,
          })),
      ];

      setRecipients(formattedRecipients);
      setLoadingRecipients(false);
    } catch (error) {
      console.error("Error loading recipients:", error);
      setNotification({
        show: true,
        title: "Error",
        message: "Failed to load recipients. Please try again.",
      });
      setLoadingRecipients(false);
    }
  };

  useEffect(() => {
    if (studioUser?.studio_id) {
      loadTemplates();
      loadClasses();
      loadEvents();
    }
  }, [studioUser?.studio_id]);

  useEffect(() => {
    if (studioUser?.studio_id) {
      loadRecipients();
    }
  }, [studioUser?.studio_id]);

  // Initialize Quill editor
  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    
    // Detect image selection in the editor
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target && target.tagName === "IMG") {
        setSelectedImage(target as HTMLImageElement);
        setImageSize({
          width: (target as HTMLImageElement).width.toString(),
          height: (target as HTMLImageElement).height.toString(),
        });
      } else {
        setSelectedImage(null);
      }
    };
    const editor = quill.root;
    editor.addEventListener("click", handleClick);
    return () => {
      editor.removeEventListener("click", handleClick);
    };
  }, [quillRef]);

  // Update image size when input changes
  const handleImageSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedImage) return;
    const { name, value } = e.target;
    setImageSize((prev) => ({ ...prev, [name]: value }));
    if (name === "width") selectedImage.width = parseInt(value) || 0;
  };

  const handleInsertTag = (tag: EmailTag) => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection();
      if (range) {
        // Ensure the tag has the proper {{}} wrapper
        const tagToInsert = tag.tag_key.startsWith('{{') ? tag.tag_key : `{{${tag.tag_key}}}`;
        editor.insertText(range.index, tagToInsert);
        editor.setSelection(range.index + tagToInsert.length, 0);
      }
    }
    setShowTagsModal(false);
  };

  // State for attached files
  const [attachedFiles, setAttachedFiles] = useState<Array<{
    id: string;
    name: string;
    url: string;
    size: number;
    type: string;
    wasOptimized?: boolean;
  }>>([]);

  // Handle file attachment from the Attach button
  const handleFileAttachment = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsCompressingImage(true);
      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error('Authentication token not available');
      }
      
      let uploadResult;
      let message;
      
      // Handle different file types
      if (file.type.startsWith('image/')) {
        // Images get compressed and uploaded
        const wasOptimized = file.size > IMAGE_MAX_FILE_SIZE;
        uploadResult = await handleImageUpload(file, studio?.id, authToken);
        message = wasOptimized
          ? "Image attached and optimized for fast email delivery!"
          : "Image attached successfully!";

        // Add to attached files list
        const newAttachment = {
          id: Date.now().toString(),
          name: file.name,
          url: uploadResult.url,
          size: uploadResult.size,
          type: file.type,
          wasOptimized,
        };

        setAttachedFiles(prev => [...prev, newAttachment]);
      } else {
        // Other files get uploaded directly (we'll need to create a general upload function)
        uploadResult = await handleFileUpload(file, studio?.id, authToken);
        message = "File attached successfully!";

        const newAttachment = {
          id: Date.now().toString(),
          name: file.name,
          url: uploadResult.url,
          size: uploadResult.size,
          type: file.type,
        };

        setAttachedFiles(prev => [...prev, newAttachment]);
      }

      setNotification({
        show: true,
        title: "Success",
        message: message,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to attach file:', error);
      setNotification({
        show: true,
        title: "Error",
        message: "Failed to attach file. Please try again.",
      });
    } finally {
      setIsCompressingImage(false);
      // Reset the input
      event.target.value = '';
    }
  };

  // Remove attached file
  const removeAttachedFile = (id: string) => {
    setAttachedFiles(prev => prev.filter(file => file.id !== id));
  };

  // Content sanitization function to remove potentially dangerous HTML
  const sanitizeHTML = (html: string) => {
    // Remove potentially dangerous tags and attributes
    return html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, "")
      .replace(/javascript:/gi, "")
      .replace(/on\w+\s*=/gi, "");
  };

  // HTML escape function for safe text insertion
  const escapeHtml = (text: string) => {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  };

  // Content filtering and validation for AI input
  const validateAIInput = (input: string) => {
    const blockedWords = [
      "password",
      "credit card",
      "ssn",
      "social security",
      "bank account",
      "routing number",
      "pin number",
      "cvv",
      "driver license",
    ];
    const lowerInput = input.toLowerCase();

    for (const word of blockedWords) {
      if (lowerInput.includes(word)) {
        throw new Error("Input contains sensitive information");
      }
    }

    if (input.length > 1000) {
      throw new Error("Input too long. Please keep it under 1000 characters");
    }

    if (input.trim().length === 0) {
      throw new Error("Please enter some text to generate content");
    }

    return true;
  };

  const handleEditorChange = (content: string) => {
    const sanitizedContent = sanitizeHTML(content);
    setEditorContent(sanitizedContent);
  };

  const handleSend = async () => {
    if (!studioUser?.studio_id) {
      setNotification({
        show: true,
        message: "No studio ID found",
        title: "Error",
      });
      return;
    }

    const hasSelectedIds = selectedRecipientIds.length > 0;
    const hasValidCustomEmail = selectedRecipientEmails.some(
      (e) => RECIPIENT_EMAIL_REGEX.test(e.trim()) && e.trim().length <= 254
    );
    if (!hasSelectedIds && !hasValidCustomEmail) {
      setNotification({
        show: true,
        message: "Please select at least one recipient or add a valid email address",
        title: "Error",
      });
      return;
    }

    // Enhanced subject validation
    if (!subject.trim()) {
      setNotification({
        show: true,
        title: "Error",
        message: "Please enter a subject for your email.",
      });
      return;
    }

    // Enhanced subject validation
    if (!validateInput(subject, 'subject')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'subject', value: subject.substring(0, 50) + '...' });
      setNotification({
        show: true,
        title: "Error",
        message: "Invalid subject content. Please check your subject and try again.",
      });
      return;
    }

    // Enhanced editor content validation
    if (!validateInput(editorContent, 'editorContent')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'editorContent', value: editorContent.substring(0, 50) + '...' });
      setNotification({
        show: true,
        title: "Error",
        message: "Invalid email content. Please check your message and try again.",
      });
      return;
    }

    const authToken = getAuthToken();
    if (!authToken) {
      setNotification({
        show: true,
        message: "Authentication token not available",
        title: "Error",
      });
      return;
    }

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        title: "Error",
        message: "Please wait before sending another email",
      });
      return;
    }

    // CSRF token validation (strict in production, flexible in development)
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'sendEmail' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          title: "Error",
          message: "Security token missing. Please refresh the page and try again.",
        });
        return;
      } else {
        // In development, log the issue but allow submission to continue
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    setLastSubmissionTime(now);
    setIsSending(true);

    // First check if Gmail is configured
    const gmailCheckResponse = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/auth/gmail/status`,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );

    if (!gmailCheckResponse.ok) {
      setNotification({
        show: true,
        message: "Failed to check Gmail status",
        title: "Error",
      });
      return;
    }

    const { isConfigured } = await gmailCheckResponse.json();

    if (!isConfigured) {
      setNotification({
        show: true,
        message:
          "Gmail connection has expired. Please reconnect your Gmail account in Settings.",
        title: "Gmail Connection Required",
      });
      return;
    }

    // Check content size before sending
    const hasEmbeddedImages = /data:image\/[^;]+;base64,/.test(editorContent);
    const maxLength = hasEmbeddedImages ? 400000 : 50000; // 400KB for images, 50KB for text
    
    if (editorContent.length > maxLength) {
      const limitText = hasEmbeddedImages ? "400KB" : "50KB";
      setNotification({
        show: true,
        message: `Email content is too large (${limitText} limit). ${hasEmbeddedImages ? 'Please reduce image sizes or use fewer images.' : 'Please shorten your message.'}`,
        title: "Content Too Large",
      });
      return;
    }

    // Build tag data for personalization
    const tagDataByRecipient: Record<string, Record<string, string>> = {};

    // Use selectedRecipientIds to ensure we get the correct recipient for each email
    for (const recipientId of selectedRecipientIds) {
      const recipient = recipients.find((r) => r.id === recipientId);
      if (!recipient || !recipient.email) continue;

      tagDataByRecipient[recipient.email] = buildRecipientTagData(studio, recipient);
    }

    const coveredEmailsLower = new Set(
      Object.keys(tagDataByRecipient).map((k) => k.toLowerCase())
    );
    for (const raw of selectedRecipientEmails) {
      const trimmed = raw.trim();
      if (!trimmed || !RECIPIENT_EMAIL_REGEX.test(trimmed) || trimmed.length > 254) {
        continue;
      }
      if (coveredEmailsLower.has(trimmed.toLowerCase())) {
        continue;
      }
      tagDataByRecipient[trimmed] = buildCustomRecipientTagData(studio, trimmed);
      coveredEmailsLower.add(trimmed.toLowerCase());
    }

    if (Object.keys(tagDataByRecipient).length === 0) {
      setNotification({
        show: true,
        message: "Please select at least one recipient or add a valid email address",
        title: "Error",
      });
      setIsSending(false);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/emails/send`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            studio_id: studioUser.studio_id,
            subject: sanitizeInput(subject, 'subject'),
            content: sanitizeInput(editorContent, 'editorContent'),
            recipients: Object.keys(tagDataByRecipient), // Use the emails from tagDataByRecipient
            tagDataByRecipient,
            attachments: attachedFiles.map(file => ({
              name: file.name,
              url: file.url,
              size: file.size
            })),
          }),
        }
      );

      if (response.ok) {
        setNotification({
          show: true,
          message: "Message sent to recipients.",
          title: "Success",
        });
        setSubject("");
        setEditorContent("");
        setSelectedRecipientEmails([]);
        setSelectedRecipientIds([]);
        setSelectedRecipients([]); // Clear modal selections too
        setAttachedFiles([]); // Clear attached files
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to send email");
      }
    } catch (error) {
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : "Failed to send email",
        title: "Error",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleNewMessage = () => {
    setSelectedRecipients([]);
    setCustomEmailInput("");
    setIsRecipientModalOpen(true);
  };

  const handleSelectRecipients = () => {
    // Pre-populate selectedRecipients with currently selected recipients
    const currentRecipientIds = recipients
      .filter((r) => selectedRecipientIds.includes(r.id))
      .map((r) => r.id);
    setSelectedRecipients(currentRecipientIds);
    setCustomEmailInput("");
    setIsRecipientModalOpen(true);
  };

  const handleRecipientModalClose = () => {
    setIsRecipientModalOpen(false);
    setSelectedRecipients([]);
    setSearchTerm("");
    setCustomEmailInput("");
  };

  const handleAddCustomEmailToSelection = () => {
    const trimmed = customEmailInput.trim();
    if (!trimmed) {
      return;
    }
    if (trimmed.length > MAX_LENGTHS.customEmail) {
      setNotification({
        show: true,
        message: "Email address is too long.",
        title: "Error",
      });
      return;
    }
    if (!RECIPIENT_EMAIL_REGEX.test(trimmed)) {
      setNotification({
        show: true,
        message: "Please enter a valid email address.",
        title: "Error",
      });
      return;
    }
    const lower = trimmed.toLowerCase();
    const emailsFromSelectedIds = selectedRecipientIds
      .map((id) => recipients.find((r) => r.id === id)?.email)
      .filter((e): e is string => Boolean(e));
    if (emailsFromSelectedIds.some((e) => e.toLowerCase() === lower)) {
      setNotification({
        show: true,
        message: "That address is already selected in the list.",
        title: "Error",
      });
      return;
    }
    if (
      selectedRecipientEmails.some((e) => e.trim().toLowerCase() === lower)
    ) {
      setNotification({
        show: true,
        message: "That address is already added.",
        title: "Error",
      });
      return;
    }
    setSelectedRecipientEmails((prev) => [...prev, trimmed]);
    setCustomEmailInput("");
  };



  const handleRecipientSelect = (recipientId: string) => {
    setSelectedRecipients((prev) => {
      if (prev.includes(recipientId)) {
        return prev.filter((id) => id !== recipientId);
      } else {
        return [...prev, recipientId];
      }
    });
  };

  const handleSelectAll = () => {
    setSelectedRecipients(filteredRecipients.map((recipient) => recipient.id));
  };

  const handleRecipientsConfirm = () => {
    // Get the emails and IDs of newly selected recipients
    const newSelectedEmails = recipients
      .filter((recipient) => selectedRecipients.includes(recipient.id))
      .map((recipient) => recipient.email);
    const newSelectedIds = selectedRecipients;
    
    // Add new emails and IDs to existing ones (avoid duplicates)
    setSelectedRecipientEmails(prev => {
      const combined = [...prev, ...newSelectedEmails];
      return Array.from(new Set(combined)); // Remove duplicates
    });
    setSelectedRecipientIds(prev => {
      const combined = [...prev, ...newSelectedIds];
      return Array.from(new Set(combined)); // Remove duplicates
    });
    
    setIsRecipientModalOpen(false);
    setSearchTerm("");
    setCustomEmailInput("");
  };

  const handleClassModalOpen = () => {
    setShowClassModal(true);
  };

  const handleClassModalClose = () => {
    setShowClassModal(false);
    setSelectedClasses([]);
  };

  const handleClassSelect = (classId: string) => {
    setSelectedClasses((prev) => {
      if (prev.includes(classId)) {
        return prev.filter((id) => id !== classId);
      } else {
        return [...prev, classId];
      }
    });
  };

  const handleSelectAllClasses = () => {
    setSelectedClasses(filteredClasses.map((classItem) => classItem.id));
  };

  const handleClassesConfirm = async () => {
    if (selectedClasses.length === 0) {
      setNotification({
        show: true,
        message: "Please select at least one class",
        title: "Error",
      });
      return;
    }

    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      // Get all students assigned to the selected classes
      const studentEmails: string[] = [];
      
      for (const classId of selectedClasses) {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/assigned-to-class/${classId}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (response.ok) {
          const students = await response.json();
          // Get full student data to access email
          const studentIds = students.map((s: any) => s.id);
          const fullStudents = recipients.filter(
            (r) =>
              r.type === "student" &&
              r.emailKind !== "alternative" &&
              studentIds.includes(r.studentId || r.id)
          );
          studentEmails.push(...fullStudents.map(s => s.email));
        }
      }

      // Remove duplicates
      const uniqueEmails = Array.from(new Set(studentEmails));
      setSelectedRecipientEmails(uniqueEmails);
      // Also update the IDs for display
      const selectedIds = recipients
        .filter(r => uniqueEmails.includes(r.email))
        .map(r => r.id);
      setSelectedRecipientIds(selectedIds);
      setShowClassModal(false);
      setSelectedClasses([]);

      setNotification({
        show: true,
        message: `Added ${uniqueEmails.length} students from selected classes`,
        title: "Success",
      });
    } catch (error) {
      setNotification({
        show: true,
        message: "Failed to load students from selected classes",
        title: "Error",
      });
    }
  };

  const handleEventModalOpen = () => {
    setShowEventModal(true);
  };

  const handleEventModalClose = () => {
    setShowEventModal(false);
    setSelectedEvents([]);
  };

  const handleEventSelect = (eventId: string) => {
    setSelectedEvents((prev) => {
      if (prev.includes(eventId)) {
        return prev.filter((id) => id !== eventId);
      } else {
        return [...prev, eventId];
      }
    });
  };

  const handleSelectAllEvents = () => {
    setSelectedEvents(filteredEvents.map((event) => event.id));
  };

  const handleEventsConfirm = async () => {
    if (selectedEvents.length === 0) {
      setNotification({
        show: true,
        message: "Please select at least one event",
        title: "Error",
      });
      return;
    }

    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      // Get all students registered for the selected events
      const studentEmails: string[] = [];
      
      for (const eventId of selectedEvents) {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/events/${eventId}/registrations`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (response.ok) {
          const registrations = await response.json();
          // Extract email addresses from registration data
          const emails = registrations
            .map((reg: any) => {
              // Handle different possible structures of registration_data
              const data = reg.registration_data;
              if (typeof data === 'object' && data !== null) {
                // Try different possible field names for email
                return data.email || data.email_address || data.contact_email || data.user_email;
              }
              return null;
            })
            .filter((email: string) => email); // Just filter out null/undefined emails
          studentEmails.push(...emails);
        }
      }

      // Remove duplicates
      const uniqueEmails = Array.from(new Set(studentEmails));
      setSelectedRecipientEmails(uniqueEmails);
      // Also update the IDs for display
      const selectedIds = recipients
        .filter(r => uniqueEmails.includes(r.email))
        .map(r => r.id);
      setSelectedRecipientIds(selectedIds);
      setShowEventModal(false);
      setSelectedEvents([]);

      setNotification({
        show: true,
        message: `Added ${uniqueEmails.length} students from selected events`,
        title: "Success",
      });
    } catch (error) {
      setNotification({
        show: true,
        message: "Failed to load students from selected events",
        title: "Error",
      });
    }
  };

  const handleAIModalClose = () => {
    setIsAIModalOpen(false);
    setAIInput("");
    setAIOutput("");
  };

  const handleAIWrite = async () => {
    const authToken = getAuthToken();
    if (!studioUser || !authToken) {
      return;
    }

    // Enhanced AI input validation
    if (!aiInput.trim()) {
      setNotification({
        show: true,
        message: "Please enter some text to generate content",
        title: "Error",
      });
      return;
    }

    // Enhanced AI input validation
    if (!validateInput(aiInput, 'aiInput')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'aiInput', value: aiInput.substring(0, 50) + '...' });
      setNotification({
        show: true,
        message: "Invalid input content. Please check your text and try again.",
        title: "Error",
      });
      return;
    }

    // Client-side rate limiting for AI requests
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        title: "Error",
        message: "Please wait before making another AI request",
      });
      return;
    }

    setLastSubmissionTime(now);

    try {
      setIsGenerating(true);
      const authToken = getAuthToken();
      if (!authToken) {
        setNotification({
          show: true,
          message: "Please log in to use AI features",
          title: "Authentication Required",
        });
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai/generate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            messages: [
              {
                role: "system",
                content:
                  "You write short, simple emails for a martial arts studio to send to students and parents. Use everyday language. No corporate tone, no marketing fluff, and no lines like \"I hope this email finds you well.\" Keep the body to a few short sentences. First line must be: Subject: <short subject>. Then a blank line, then the email body. Plain text only. No HTML. No extra commentary.",
              },
              {
                role: "user",
                content: `Write a simple email about: ${sanitizeInput(aiInput, "aiInput")}`,
              },
            ],
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 429) {
          throw new Error(
            errorData.error ||
              "AI rate limit exceeded. Please wait before trying again."
          );
        } else if (response.status === 401) {
          throw new Error(
            "Authentication expired. Please refresh the page and try again."
          );
        } else {
          throw new Error(errorData.error || "Failed to generate content");
        }
      }

      const data = await response.json();
      setAIOutput(data.content || "");
    } catch (error) {
      setNotification({
        show: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to generate email content. Please try again.",
        title: "Error",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUseInEditor = () => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      
      // Split the AI output into lines
      const lines = aiOutput.split("\n");
      
      // Try to extract subject from the first non-empty line
      let subjectLine = "";
      let bodyLines = [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line && !subjectLine) {
          // First non-empty line might be the subject
          // Check if it looks like a subject (not too long, ends with common subject patterns)
          if (line.length <= 100 && !line.includes("Dear") && !line.includes("Hello") && !line.includes("Hi")) {
            subjectLine = line;
            // Skip this line in body content
            continue;
          }
        }
        bodyLines.push(line);
      }
      
      // If we found a potential subject, set it
      if (subjectLine) {
        // Remove "Subject:" prefix if present and clean up
        const cleanSubject = subjectLine
          .replace(/^Subject:\s*/i, '') // Remove "Subject:" prefix (case insensitive)
          .replace(/^Subject\s*/i, '')  // Remove "Subject" prefix (case insensitive)
          .trim();
        setSubject(cleanSubject);
      }
      
      // Process body content with proper spacing
      const sanitizedContent = bodyLines
        .map((line) => {
          const escapedLine = escapeHtml(line);
          // If line is empty, still create a paragraph for spacing
          return escapedLine.trim() === "" ? "<p><br></p>" : `<p>${escapedLine}</p>`;
        })
        .join("");
      
      quill.clipboard.dangerouslyPasteHTML(sanitizedContent);
      handleAIModalClose();
    }
  };

  const handleCopyContent = () => {
    navigator.clipboard.writeText(aiOutput);
  };

  const handleDiscard = () => {
    setSelectedRecipients([]);
    setSelectedRecipientEmails([]);
    setSelectedRecipientIds([]);
    setSubject("");
    setEditorContent("");
    setShowDiscardModal(false);
  };

  const cancelDiscard = () => {
    setShowDiscardModal(false);
  };

  const filteredRecipients = recipients.filter(
    (recipient) =>
      (recipient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        recipient.email.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (typeFilter === "all"
        ? true
        : typeFilter === "alternative"
        ? recipient.type === "student" && recipient.emailKind === "alternative"
        : recipient.type === typeFilter &&
          recipient.emailKind !== "alternative")
  );

  const filteredClasses = classes.filter(
    (classItem) =>
      classItem.class_name.toLowerCase().includes(classSearchTerm.toLowerCase()) ||
      (classItem.days && classItem.days.some(day => 
        day.toLowerCase().includes(classSearchTerm.toLowerCase())
      ))
  );

  const filteredEvents = events.filter(
    (event) =>
      event.event_name.toLowerCase().includes(eventSearchTerm.toLowerCase()) ||
      event.from_date.toLowerCase().includes(eventSearchTerm.toLowerCase()) ||
      event.end_date.toLowerCase().includes(eventSearchTerm.toLowerCase())
  );

  const handleSaveTemplate = async () => {
    // Enhanced template name validation
    if (!templateName.trim()) {
      setNotification({
        show: true,
        title: "Error",
        message: "Please enter a template name",
      });
      return;
    }

    // Enhanced template name validation
    if (!validateInput(templateName, 'templateName')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'templateName', value: templateName });
      setNotification({
        show: true,
        title: "Error",
        message: "Invalid template name. Please use only letters, numbers, spaces, hyphens, and underscores.",
      });
      return;
    }

    if (emailTemplatesLimitReached) {
      setNotification({
        show: true,
        message: `You can only create up to ${emailTemplatesLimit} email templates per studio.`,
        title: "Limit Reached",
      });
      return;
    }

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        title: "Error",
        message: "Please wait before saving another template",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'saveTemplate' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          title: "Error",
          message: "Security token missing. Please refresh the page and try again.",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    setLastSubmissionTime(now);

    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/email-templates`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            name: sanitizeInput(templateName, 'templateName'),
            template: sanitizeInput(editorContent, 'editorContent'),
            studio_id: studioUser?.studio_id,
            csrfToken,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save template");
      }

      setNotification({
        show: true,
        message: "Template saved successfully",
        title: "Success",
      });
      setShowSaveTemplateModal(false);
      setTemplateName("");
      loadTemplates();
    } catch (error) {
      setNotification({
        show: true,
        message: "Failed to save template",
        title: "Error",
      });
    }
  };

  const handleCancelSaveTemplate = () => {
    setShowSaveTemplateModal(false);
    setTemplateName("");
  };

  const handleScheduleEmail = async () => {
    // Validate date and time
    if (!scheduledDate || !scheduledTime) {
      setNotification({
        show: true,
        title: "Missing Information",
        message: "Please select both date and time",
      });
      return;
    }

    // Validate date format
    if (!validateInput(scheduledDate, 'scheduledDate')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'scheduledDate', value: scheduledDate });
      setNotification({
        show: true,
        title: "Invalid Date",
        message: "Please use MM/DD/YYYY format",
      });
      return;
    }

    // Validate time format
    if (!validateInput(scheduledTime, 'scheduledTime')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'scheduledTime', value: scheduledTime });
      setNotification({
        show: true,
        title: "Invalid Time",
        message: "Please use HH:MM format",
      });
      return;
    }

    // Validate recipients
    if (selectedRecipientIds.length === 0) {
      setNotification({
        show: true,
        title: "No Recipients",
        message: "Please select at least one recipient",
      });
      return;
    }

    // Validate subject
    if (!subject.trim()) {
      setNotification({
        show: true,
        title: "Missing Subject",
        message: "Please enter a subject for your email",
      });
      return;
    }

    // Validate content
    if (!editorContent.trim()) {
      setNotification({
        show: true,
        title: "Missing Content",
        message: "Please enter content for your email",
      });
      return;
    }

    setIsScheduling(true);

    debugLog('[Frontend] Scheduling email...');
    debugLog('[Frontend] Input date:', scheduledDate);
    debugLog('[Frontend] Input time:', scheduledTime);
    debugLog('[Frontend] Input AM/PM:', scheduledAMPM);
    debugLog('[Frontend] Studio timezone:', studioTimezone);

    try {
      // Convert local time to UTC for storage
      const scheduledDateTime = convertToUTC(scheduledDate, scheduledTime, scheduledAMPM, studioTimezone);
      
      debugLog('[Frontend] Converted UTC time:', scheduledDateTime);
      debugLog('[Frontend] Current time:', new Date().toISOString());
      debugLog('[Frontend] Time difference (minutes):', Math.round((new Date(scheduledDateTime).getTime() - new Date().getTime()) / 60000));

      // Build tag data for personalization
      const tagDataByRecipient: Record<string, Record<string, string>> = {};

      // Use selectedRecipientIds to ensure we get the correct recipient for each email
      for (const recipientId of selectedRecipientIds) {
        const recipient = recipients.find((r) => r.id === recipientId);
        if (!recipient || !recipient.email) continue;

        tagDataByRecipient[recipient.email] = buildRecipientTagData(studio, recipient);
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/emails/schedule`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            studio_id: studioUser?.studio_id,
            subject: sanitizeInput(subject, 'subject'),
            content: sanitizeInput(editorContent, 'editorContent'),
            recipients: Object.keys(tagDataByRecipient), // Use the emails from tagDataByRecipient
            scheduled_time: scheduledDateTime,
            tagDataByRecipient,
            attachments: attachedFiles.map(file => ({
              name: file.name,
              url: file.url,
              size: file.size
            })),
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to schedule email");
      }

      // Format the scheduled time for display
      const scheduledDateObj = new Date(scheduledDateTime);
      const formattedDate = scheduledDateObj.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      const formattedTime = scheduledDateObj.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        timeZoneName: 'short'
      });
      
      setNotification({
        show: true,
        title: "Email Scheduled",
        message: `Email will be sent on ${formattedDate} at ${formattedTime}`,
      });

      // Reset form
      setShowScheduleModal(false);
      setScheduledDate("");
      setScheduledTime("");
      setScheduledAMPM("AM");
      setSubject("");
      setEditorContent("");
      setSelectedRecipientEmails([]);
      setSelectedRecipientIds([]);
      setAttachedFiles([]); // Clear attached files
    } catch (error) {
      setNotification({
        show: true,
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to schedule email",
      });
    } finally {
      setIsScheduling(false);
    }
  };

  const handleCancelSchedule = () => {
    setShowScheduleModal(false);
    setScheduledDate("");
    setScheduledTime("");
    setScheduledAMPM("AM");
    setIsScheduling(false);
  };

  const handleTemplateSelect = (template: Template) => {
    const sanitizedTemplate = sanitizeHTML(template.template);
    setEditorContent(sanitizedTemplate);
    setShowTemplatesModal(false);
  };

  const filteredTemplates = templates.filter((template) =>
    template.name.toLowerCase().includes(templateSearchTerm.toLowerCase())
  );



  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        title: "Error",
        message: "Please wait before deleting another template",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'deleteTemplate' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          title: "Error",
          message: "Security token missing. Please refresh the page and try again.",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    setLastSubmissionTime(now);

    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/email-templates/${templateToDelete.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete template");
      }

      setNotification({
        show: true,
        message: "Template deleted successfully",
        title: "Success",
      });
      setTemplateToDelete(null);
      loadTemplates();
    } catch (error) {
      setNotification({
        show: true,
        message: "Failed to delete template",
        title: "Error",
      });
    }
  };

  return (
    <PlanGatedPage
      capability="messagingEmail"
      title="Email Messaging"
      subtitle="Send emails to your students and leads"
    >
    <div className="relative bg-gray-50 dark:bg-gray-900">
      <div className="space-y-4 p-0 pt-6 xl:pt-0 xl:p-6">
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
        />

        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Email
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Send emails to your students and leads
            </p>
          </div>
        </div>

        {/* Gmail Connection Banner - Only show if check is complete and not connected */}
        {gmailCheckComplete && !isGmailConnected && (
          <div className="mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded flex flex-col items-center text-center">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Connect Gmail to Send Messages
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Please connect your Gmail or Google Workspace account in the
              settings to send emails to students and leads.
            </p>
            <Link
              to="/app/settings/integrations"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Go to Integrations Settings
            </Link>
          </div>
        )}

        <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 rounded-lg shadow">
          {/* Recipients Section */}
          <div className="border-b border-gray-200 dark:border-gray-700 p-4">
            <div className="flex flex-col xl:flex-row xl:justify-between xl:items-center gap-4 mb-4">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                Recipients
              </h2>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleClassModalOpen}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition cursor-pointer"
                >
                  <AcademicCapIcon className="h-4 w-4 mr-2 pointer-events-none" />
                  <span className="pointer-events-none">Select by Class</span>
                </button>
                <button
                  type="button"
                  onClick={handleEventModalOpen}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition cursor-pointer"
                >
                  <CalendarDaysIcon className="h-4 w-4 mr-2 pointer-events-none" />
                  <span className="pointer-events-none">Select by Event</span>
                </button>
                <button
                  type="button"
                  onClick={handleSelectRecipients}
                  disabled={loadingRecipients}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingRecipients ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b border-indigo-600 mr-2 pointer-events-none"></div>
                  ) : (
                    <PlusIcon className="h-4 w-4 mr-2 pointer-events-none" />
                  )}
                  <span className="pointer-events-none">{loadingRecipients ? "Loading..." : "Select Recipients"}</span>
                </button>
              </div>
            </div>
            <div>
              <label
                htmlFor="recipients"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                To
              </label>
              <div className="space-y-2">
                {selectedRecipientIds.length > 0 || selectedRecipientEmails.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {/* Display recipients by ID (matched recipients) */}
                    {selectedRecipientIds.map((recipientId, index) => {
                      // Find the recipient by ID to get their name
                      const recipient = recipients.find(r => r.id === recipientId);
                      const displayName = recipient
                        ? recipient.emailKind === "alternative"
                          ? `${recipient.name} (alt)`
                          : recipient.name
                        : "Unknown";
                      
                      return (
                        <span
                          key={`${recipientId}-${index}`}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300"
                        >
                          {displayName}
                          <button
                            onClick={() => {
                              const newIds = selectedRecipientIds.filter((_, i) => i !== index);
                              setSelectedRecipientIds(newIds);
                              // Also update the emails array
                              const newEmails = recipients
                                .filter(r => newIds.includes(r.id))
                                .map(r => r.email);
                              setSelectedRecipientEmails(newEmails);
                            }}
                            className="ml-1 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 cursor-pointer"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                    {/* Display emails that don't match any recipient */}
                    {selectedRecipientEmails
                      .filter(email => !recipients.some(r => r.email === email))
                      .map((email, index) => (
                        <span
                          key={`email-${index}`}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300"
                        >
                          {email}
                          <button
                            onClick={() => {
                              const newEmails = selectedRecipientEmails.filter((_, i) => 
                                recipients.some(r => r.email === selectedRecipientEmails[i]) || 
                                selectedRecipientEmails[i] !== email
                              );
                              setSelectedRecipientEmails(newEmails);
                              // Update IDs based on remaining emails
                              const newIds = recipients
                                .filter(r => newEmails.includes(r.email))
                                .map(r => r.id);
                              setSelectedRecipientIds(newIds);
                            }}
                            className="ml-1 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 cursor-pointer"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                  </div>
                ) : (
                  <input
                    type="text"
                    id="recipients"
                    value=""
                    readOnly
                    className="mt-1 block w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Select recipients"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Message Section */}
          <div className="flex-1 p-4">
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="subject"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  value={subject}
                  onChange={(e) => {
                    const sanitizedValue = sanitizeInput(e.target.value, 'subject');
                    if (sanitizedValue.length <= MAX_LENGTHS.subject) {
                      setSubject(sanitizedValue);
                    }
                  }}
                  maxLength={MAX_LENGTHS.subject}
                  className="mt-1 block w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter subject"
                />
              </div>

              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Tooltip text="You can use student, lead, and staff tags in the same email! The system automatically filters tags based on recipient type, so you can send one email to mixed groups without creating multiple versions.">
                    <span className="cursor-help">
                      Message
                    </span>
                  </Tooltip>
                </label>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowTagsModal(true)}
                    className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
                  >
                    <span className="pointer-events-none">Insert Tag</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowTemplatesModal(true)}
                    className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
                  >
                    <span className="pointer-events-none">Templates</span>
                  </button>
                </div>
              </div>

              <div className="resizable-editor quill-container">
                <CustomToolbar
                  imageSize={imageSize}
                  onImageSizeChange={handleImageSizeChange}
                  showImageControls={!!selectedImage}
                />
                <ReactQuill
                  theme="snow"
                  value={editorContent}
                  onChange={handleEditorChange}
                  modules={modules}
                  formats={formats}
                  className="h-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Write your message here..."
                  ref={quillRef}
                />


                {/* Attached Files Display */}
                {attachedFiles.length > 0 && (
                  <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Attached Files ({attachedFiles.length})
                    </h4>
                    <div className="space-y-2">
                      {attachedFiles.map((file) => (
                        <div key={file.id} className="flex items-center justify-between p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                          <div className="flex items-center space-x-2">
                            {file.type.startsWith('image/') ? (
                              <img 
                                src={file.url} 
                                alt={file.name}
                                className="w-8 h-8 object-cover rounded"
                              />
                            ) : (
                              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center">
                                <DocumentIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                              </div>
                            )}
                            <div>
                              <p className="text-sm text-gray-900 dark:text-white font-medium">{file.name}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {(file.size / 1024).toFixed(1)} KB
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => removeAttachedFile(file.id)}
                            className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                            title="Remove attachment"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-4">
            <div className="flex flex-wrap gap-2 items-center">
              <button
                type="button"
                onClick={() => setShowScheduleModal(true)}
                disabled={selectedRecipientIds.length === 0}
                title={selectedRecipientIds.length === 0 ? "Please select at least one recipient to schedule your message" : ""}
                className={`inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md ${
                  selectedRecipientIds.length === 0
                    ? "text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
                    : "text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                }`}
              >
                <ClockIcon className="h-4 w-4 mr-2 pointer-events-none" />
                <span className="pointer-events-none">Schedule</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!emailTemplatesLimitReached) {
                    setShowSaveTemplateModal(true);
                  } else {
                    setNotification({
                      show: true,
                      message: `You can only create up to ${emailTemplatesLimit} email templates per studio.`,
                      title: "Limit Reached",
                    });
                  }
                }}
                disabled={emailTemplatesLimitReached}
                className={`inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md ${
                  emailTemplatesLimitReached
                    ? "text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
                    : "text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                }`}
                title={emailTemplatesLimitReached ? `You can only create up to ${emailTemplatesLimit} email templates per studio.` : ""}
              >
                <DocumentTextIcon className="h-4 w-4 mr-2 pointer-events-none" />
                <span className="pointer-events-none">Save Template {templates.length}/{emailTemplatesLimit}</span>
              </button>
              <button
                type="button"
                onClick={() => document.getElementById('file-upload-input')?.click()}
                disabled={isCompressingImage}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                title="Attach a file to your email"
              >
                <PaperClipIcon className="h-4 w-4 mr-2 pointer-events-none" />
                <span className="pointer-events-none">
                  {isCompressingImage ? 'Attaching…' : 'Attach File'}
                </span>
              </button>
              <input
                id="file-upload-input"
                type="file"
                accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xls,.xlsx"
                onChange={handleFileAttachment}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                onClick={() => setShowDiscardModal(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
              >
                <TrashIcon className="h-4 w-4 mr-2 pointer-events-none" />
                <span className="pointer-events-none">Discard</span>
              </button>
              {hasCapability('aiCompose') && (
              <button
                type="button"
                onClick={() => setIsAIModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
              >
                <SparklesIcon className="h-4 w-4 mr-2 pointer-events-none" />
                <span className="pointer-events-none">Write with AI</span>
              </button>
              )}
              <button
                type="button"
                onClick={handleSend}
                disabled={isSending}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {isSending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 flex-shrink-0 pointer-events-none"></div>
                ) : (
                  <PaperAirplaneIcon className="h-4 w-4 mr-2 flex-shrink-0 pointer-events-none" />
                )}
                <span className="pointer-events-none">{isSending ? "Sending..." : "Send"}</span>
              </button>
            </div>

            <button
              type="button"
              onClick={() => setShowEmailImageSpecs((prev) => !prev)}
              className="mt-2 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 underline-offset-2 hover:underline text-left"
            >
              {showEmailImageSpecs
                ? 'Hide tips for attaching photos to your email'
                : 'What image size works best for email attachments?'}
            </button>
            <EmailImageAttachmentTips
              attachments={attachedFiles}
              expanded={showEmailImageSpecs}
            />
            
            {/* Character counter - moved outside editor, only show when approaching limit */}
            {(() => {
              const hasEmbeddedImages = /data:image\/[^;]+;base64,/.test(editorContent);
              const maxLength = hasEmbeddedImages ? 400000 : 50000; // 400KB for images, 50KB for text
              const currentLength = editorContent.length;
              const percentage = (currentLength / maxLength) * 100;
              const limitText = hasEmbeddedImages ? "400KB" : "50KB";
              
              // Only show counter when content is > 50% of limit
              if (percentage > 50) {
                let colorClass = "text-gray-500 dark:text-gray-400";
                if (percentage > 90) {
                  colorClass = "text-red-600 dark:text-red-400";
                } else if (percentage > 75) {
                  colorClass = "text-yellow-600 dark:text-yellow-400";
                } else {
                  colorClass = "text-blue-600 dark:text-blue-400";
                }
                
                return (
                  <div className="mt-3 text-right">
                    <span className={`text-xs ${colorClass}`}>
                      {currentLength.toLocaleString()} / {limitText} 
                      {hasEmbeddedImages && " (with images)"}
                    </span>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        </div>
      </div>

      {/* AI Writing Modal */}
      {isAIModalOpen && hasCapability('aiCompose') && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full max-w-xl xl:max-w-lg sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  onClick={handleAIModalClose}
                >
                  <span className="sr-only pointer-events-none">Close</span>
                  <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                    Write with AI
                  </h3>
                  <div className="mt-4 space-y-4">
                    <div>
                      <label
                        htmlFor="ai-input"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        What would you like to write about?
                      </label>
                      <div className="mt-1 relative">
                        <textarea
                          id="ai-input"
                          rows={4}
                          value={aiInput}
                          onChange={(e) => {
                            const sanitizedValue = sanitizeInput(e.target.value, 'aiInput');
                            if (sanitizedValue.length <= MAX_LENGTHS.aiInput) {
                              setAIInput(sanitizedValue);
                            }
                          }}
                          maxLength={MAX_LENGTHS.aiInput}
                          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="e.g. remind parents about belt testing Saturday at 10am"
                        />
                        <div className="absolute bottom-2 right-2 text-xs text-gray-500 dark:text-gray-400">
                          {aiInput.length} characters
                        </div>
                      </div>
                    </div>
                    {isGenerating && (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
                          Generating content...
                        </span>
                      </div>
                    )}
                    {aiOutput && (
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <label
                            htmlFor="ai-output"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            AI Generated Content
                          </label>
                        </div>
                        <textarea
                          id="ai-output"
                          rows={6}
                          value={aiOutput}
                          readOnly
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white custom-scrollbar"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 flex flex-col xl:flex-row xl:justify-between gap-3 xl:gap-0">
                <div className="flex gap-2">
                  {aiOutput && (
                    <>
                      <button
                        type="button"
                        onClick={handleCopyContent}
                        className="inline-flex items-center px-3 py-2 text-sm text-gray-900 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:border-indigo-200 dark:hover:border-indigo-500 transition"
                      >
                        <ClipboardDocumentIcon className="h-4 w-4 mr-1 pointer-events-none" />
                        <span className="pointer-events-none">Copy</span>
                      </button>
                      <button
                        type="button"
                        onClick={handleUseInEditor}
                        className="inline-flex items-center px-3 py-2 text-sm text-indigo-700 dark:text-indigo-400 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:border-indigo-200 dark:hover:border-indigo-500 transition"
                      >
                        <DocumentTextIcon className="h-4 w-4 mr-1 pointer-events-none" />
                        <span className="pointer-events-none">Use in Editor</span>
                      </button>
                    </>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleAIWrite}
                    disabled={isGenerating}
                    className={`inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm ${
                      isGenerating
                        ? "bg-indigo-400 cursor-not-allowed"
                        : "bg-indigo-600 hover:bg-indigo-500"
                    }`}
                  >
                    <span className="pointer-events-none">{isGenerating ? "Generating..." : "Generate"}</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleAIModalClose}
                    className="inline-flex justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <span className="pointer-events-none">Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recipient Selection Modal */}
      {isRecipientModalOpen && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  onClick={handleRecipientModalClose}
                >
                  <span className="sr-only pointer-events-none">Close</span>
                  <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-left sm:ml-4 sm:mt-0 w-full">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-white">
                    Select Recipients
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Choose students, leads, and staff—or add any email address.
                  </p>
                  <div className="mt-4">
                    <div className="flex flex-col space-y-3">
                      <div className="flex items-center space-x-3">
                        <div className="flex-1">
                          <input
                            type="text"
                            placeholder="Search recipients..."
                            value={searchTerm}
                            onChange={(e) => {
                              const sanitizedValue = sanitizeInput(e.target.value, 'searchTerm');
                              if (sanitizedValue.length <= MAX_LENGTHS.searchTerm) {
                                setSearchTerm(sanitizedValue);
                              }
                            }}
                            maxLength={MAX_LENGTHS.searchTerm}
                            className={getInputClassName(
                              "block w-full rounded-md border-0 py-1.5 pl-3 pr-10 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            )}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleSelectAll}
                          className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 whitespace-nowrap"
                        >
                          <span className="pointer-events-none">Select All</span>
                        </button>
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-sm text-gray-700 dark:text-gray-300">
                          Filter by:
                        </label>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => setTypeFilter("all")}
                            className={`px-3 py-1 text-sm rounded-full border transition-colors duration-150 ${
                              typeFilter === "all"
                                ? "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 border-white dark:border-gray-600"
                                : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600"
                            }`}
                          >
                            <span className="pointer-events-none">All</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setTypeFilter("student")}
                            className={`px-3 py-1 text-sm rounded-full border transition-colors duration-150 ${
                              typeFilter === "student"
                                ? "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 border-white dark:border-gray-600"
                                : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600"
                            }`}
                          >
                            <span className="pointer-events-none">Students</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setTypeFilter("alternative")}
                            className={`px-3 py-1 text-sm rounded-full border transition-colors duration-150 ${
                              typeFilter === "alternative"
                                ? "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 border-white dark:border-gray-600"
                                : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600"
                            }`}
                          >
                            <span className="pointer-events-none">Alternative</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setTypeFilter("lead")}
                            className={`px-3 py-1 text-sm rounded-full border transition-colors duration-150 ${
                              typeFilter === "lead"
                                ? "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 border-white dark:border-gray-600"
                                : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600"
                            }`}
                          >
                            <span className="pointer-events-none">Leads</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setTypeFilter("staff")}
                            className={`px-3 py-1 text-sm rounded-full border transition-colors duration-150 ${
                              typeFilter === "staff"
                                ? "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 border-white dark:border-gray-600"
                                : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600"
                            }`}
                          >
                            <span className="pointer-events-none">Staff</span>
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-3">
                        <div className="flex-1 min-w-0">
                          <label
                            htmlFor="custom-recipient-email"
                            className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1"
                          >
                            Add custom email
                          </label>
                          <input
                            id="custom-recipient-email"
                            type="email"
                            autoComplete="email"
                            placeholder="name@example.com"
                            value={customEmailInput}
                            onChange={(e) => {
                              const sanitizedValue = sanitizeInput(
                                e.target.value,
                                "searchTerm"
                              );
                              if (sanitizedValue.length <= MAX_LENGTHS.customEmail) {
                                setCustomEmailInput(sanitizedValue);
                              }
                            }}
                            maxLength={MAX_LENGTHS.customEmail}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddCustomEmailToSelection();
                              }
                            }}
                            className={getInputClassName(
                              "block w-full rounded-md border-0 py-1.5 pl-3 pr-3 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            )}
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={handleAddCustomEmailToSelection}
                            className="inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-4 py-2 text-sm font-semibold text-indigo-600 dark:text-indigo-400 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:w-auto sm:min-w-[5rem]"
                          >
                            <span className="pointer-events-none">Add</span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 max-h-96 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-600">
                      {loadingRecipients ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        </div>
                      ) : filteredRecipients.length === 0 ? (
                        <div className="text-center py-8">
                          <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                            No recipients found
                          </h3>
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            {searchTerm
                              ? "No recipients match your search"
                              : "No recipients available"}
                          </p>
                        </div>
                      ) : (
                        filteredRecipients.map((recipient) => (
                          <div
                            key={recipient.id}
                            className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-b-0 transition-colors duration-150"
                            onClick={() => handleRecipientSelect(recipient.id)}
                          >
                            <div className="flex items-center min-w-0">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {recipient.name}
                                  <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">
                                    ({formatRecipientTypeLabel(recipient)})
                                  </span>
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                  {recipient.email}
                                </p>
                              </div>
                            </div>
                            {selectedRecipients.includes(recipient.id) && (
                              <CheckIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      onClick={handleRecipientsConfirm}
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                    >
                      <span className="pointer-events-none">Confirm</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleRecipientModalClose}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                    >
                      <span className="pointer-events-none">Cancel</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Discard Confirmation Modal */}
      {showDiscardModal && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div>
                <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white text-left">
                    Discard Message
                  </h3>
                  <div className="mt-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-left">
                      Are you sure you want to discard this message? This action
                      cannot be undone.
                    </p>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDiscard}
                  className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                >
                  <span className="pointer-events-none">Discard</span>
                </button>
                <button
                  type="button"
                  onClick={cancelDiscard}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                >
                  <span className="pointer-events-none">Cancel</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Insert Tag Modal */}
      {showTagsModal && (
        <div className="fixed inset-0 z-20 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
            <button
              type="button"
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              onClick={() => setShowTagsModal(false)}
            >
                  <span className="sr-only pointer-events-none">Close</span>
                  <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
            </button>
              </div>
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
              Insert Tag
            </h3>
            <ul className="space-y-2">
              {emailTags
                .filter((tag) => {
                  if (
                    !hasCapability('stripeBilling') &&
                    isPaymentLinkTag(tag.tag_key)
                  ) {
                    return false;
                  }
                  return (
                    tag.tag_key !== "{{lead_interest}}" &&
                    tag.tag_key !== "{{leadInterest}}" &&
                    tag.tag_key !== "{{leadInterest}}" &&
                    tag.tag_key !== "{{leadInterest}}" &&
                    tag.tag_key !== "{{staff_email}}" &&
                    tag.tag_key !== "staff_email"
                  );
                })
                .map((tag) => (
                  <li key={tag.id}>
                    <button
                      className="w-full text-left px-3 py-2 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:border-indigo-200 dark:hover:border-indigo-500 transition"
                      onClick={() => handleInsertTag(tag)}
                    >
                      <span className="font-mono text-indigo-700 dark:text-indigo-400 pointer-events-none">
                        {tag.tag_key.startsWith('{{') ? tag.tag_key : `{{${tag.tag_key}}}`}
                      </span>{" "}
                      <span className="text-gray-600 dark:text-gray-400 pointer-events-none">
                        - {tag.tag_name}
                      </span>
                    </button>
                  </li>
                ))}
            </ul>
            </div>
          </div>
        </div>
      )}

      {/* Templates Modal */}
      {showTemplatesModal && (
        <div className="fixed inset-0 z-20 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full max-w-xl xl:max-w-lg sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
            <button
              type="button"
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              onClick={() => setShowTemplatesModal(false)}
            >
                  <span className="sr-only pointer-events-none">Close</span>
                  <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
            </button>
              </div>
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
              Templates
            </h3>
            <ul className="space-y-2">
              {templates.map((tpl) => (
                <li
                  key={tpl.id}
                  className="flex items-center justify-between gap-2"
                >
                  <button
                    className="flex-1 text-left px-3 py-2 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:border-indigo-200 dark:hover:border-indigo-500 transition"
                    onClick={() => handleTemplateSelect(tpl)}
                  >
                    <span className="font-semibold text-indigo-700 dark:text-indigo-400 pointer-events-none">
                      {tpl.name}
                    </span>
                  </button>
                  <button
                    onClick={() => setTemplateToDelete(tpl)}
                    className="ml-2 px-2 py-1 text-xs rounded border border-red-400 dark:border-red-600 bg-white dark:bg-gray-700 text-red-700 dark:text-red-400 font-semibold hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-600 dark:hover:border-red-500 transition"
                  >
                    <span className="pointer-events-none">Delete</span>
                  </button>
                </li>
              ))}
            </ul>
            </div>
          </div>
        </div>
      )}

      {/* Save Template Modal */}
      {showSaveTemplateModal && !emailTemplatesLimitReached && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  onClick={handleCancelSaveTemplate}
                >
                  <span className="sr-only pointer-events-none">Close</span>
                  <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-left sm:ml-4 sm:mt-0 w-full">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-white">
                    Save Email as Template
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Save your current message as a template for future use.
                    Maximum of {emailTemplatesLimit} templates per studio.
                  </p>
                  <div className="mt-4">
                    <label
                      htmlFor="template-name"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Template Name
                    </label>
                    <input
                      type="text"
                      id="template-name"
                      value={templateName}
                      onChange={(e) => {
                        const sanitizedValue = sanitizeInput(e.target.value, 'templateName');
                        if (sanitizedValue.length <= MAX_LENGTHS.templateName) {
                          setTemplateName(sanitizedValue);
                        }
                      }}
                      maxLength={MAX_LENGTHS.templateName}
                      className={getInputClassName(
                        "mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      )}
                      placeholder="Enter template name"
                    />
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      {templates.length}/{emailTemplatesLimit} templates used
                    </p>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      onClick={handleSaveTemplate}
                      disabled={emailTemplatesLimitReached}
                      className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto ${
                        emailTemplatesLimitReached
                          ? "bg-gray-400 dark:bg-gray-600 cursor-not-allowed"
                          : "bg-indigo-600 hover:bg-indigo-500"
                      }`}
                    >
                      <span className="pointer-events-none">Save Template</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelSaveTemplate}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                    >
                      <span className="pointer-events-none">Cancel</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Email Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  onClick={handleCancelSchedule}
                >
                  <span className="sr-only pointer-events-none">Close</span>
                  <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-left sm:ml-4 sm:mt-0 w-full">
                  <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                    Schedule Email
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Schedule your email to be sent at a specific time in your studio's timezone ({studioTimezone}).
                  </p>
                  <div className="mt-4 space-y-4">
                    <div>
                      <label
                        htmlFor="schedule-date"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Date (MM/DD/YYYY)
                      </label>
                      <input
                        type="text"
                        id="schedule-date"
                        value={scheduledDate}
                        onChange={(e) => {
                          const formattedValue = formatDate(e.target.value);
                          if (formattedValue.length <= MAX_LENGTHS.scheduledDate) {
                            setScheduledDate(formattedValue);
                          }
                        }}
                        maxLength={MAX_LENGTHS.scheduledDate}
                        placeholder="MM/DD/YYYY"
                        className={getInputClassName(
                          "mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        )}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="schedule-time"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Time - {studioTimezone}
                      </label>
                      <div className="mt-1 flex space-x-2">
                        <input
                          type="text"
                          id="schedule-time"
                          value={scheduledTime}
                          onChange={(e) => {
                            const formattedValue = formatTime(e.target.value);
                            if (formattedValue.length <= MAX_LENGTHS.scheduledTime) {
                              setScheduledTime(formattedValue);
                            }
                          }}
                          maxLength={MAX_LENGTHS.scheduledTime}
                          placeholder="HH:MM"
                          className={getInputClassName(
                            "block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          )}
                        />
                        <select
                          value={scheduledAMPM}
                          onChange={(e) => setScheduledAMPM(e.target.value)}
                          className={getInputClassName(
                            "block w-20 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          )}
                        >
                          <option value="AM">AM</option>
                          <option value="PM">PM</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      onClick={handleScheduleEmail}
                      disabled={isScheduling}
                      className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm sm:ml-3 sm:w-auto ${
                        isScheduling
                          ? "bg-indigo-400 text-white cursor-not-allowed"
                          : "bg-indigo-600 text-white hover:bg-indigo-500"
                      }`}
                    >
                      <span className="pointer-events-none">{isScheduling ? "Scheduling..." : "Schedule"}</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelSchedule}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                    >
                      <span className="pointer-events-none">Cancel</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Class Selection Modal */}
      {showClassModal && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  onClick={handleClassModalClose}
                >
                  <span className="sr-only pointer-events-none">Close</span>
                  <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-left sm:ml-4 sm:mt-0 w-full">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-white">
                    Select Classes
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Choose classes to add all enrolled students to your message
                  </p>
                                      <div className="mt-4">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="flex-1">
                          <input
                            type="text"
                            placeholder="Search classes..."
                            value={classSearchTerm}
                            onChange={(e) => {
                              const sanitizedValue = sanitizeInput(e.target.value, 'classSearchTerm');
                              if (sanitizedValue.length <= MAX_LENGTHS.classSearchTerm) {
                                setClassSearchTerm(sanitizedValue);
                              }
                            }}
                            maxLength={MAX_LENGTHS.classSearchTerm}
                            className="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleSelectAllClasses}
                          className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 whitespace-nowrap"
                        >
                          <span className="pointer-events-none">Select All Classes</span>
                        </button>
                      </div>
                      <div className="max-h-96 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-600">
                      {loadingClasses ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        </div>
                                              ) : filteredClasses.length === 0 ? (
                          <div className="text-center py-8">
                            <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                              {classSearchTerm ? "No classes match your search" : "No classes found"}
                            </h3>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                              {classSearchTerm ? "Try adjusting your search terms" : "No classes available for this studio"}
                            </p>
                          </div>
                        ) : (
                          filteredClasses.map((classItem) => (
                          <div
                            key={classItem.id}
                            className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-b-0 transition-colors duration-150"
                            onClick={() => handleClassSelect(classItem.id)}
                          >
                            <div className="flex items-center min-w-0">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {classItem.class_name}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                  {classItem.days && classItem.days.length > 0 && (
                                    <span>{classItem.days.join(", ")}</span>
                                  )}
                                  {classItem.start_time && classItem.end_time && (
                                    <span className="ml-2">
                                      {formatTime12Hour(classItem.start_time)} - {formatTime12Hour(classItem.end_time)}
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                            {selectedClasses.includes(classItem.id) && (
                              <CheckIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      onClick={handleClassesConfirm}
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                    >
                      <span className="pointer-events-none">Confirm</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleClassModalClose}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                    >
                      <span className="pointer-events-none">Cancel</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Event Selection Modal */}
      {showEventModal && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  onClick={handleEventModalClose}
                >
                  <span className="sr-only pointer-events-none">Close</span>
                  <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-left sm:ml-4 sm:mt-0 w-full">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-white">
                    Select Events
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Choose events to add all registrants to your message
                  </p>
                  <div className="mt-4">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="Search events..."
                          value={eventSearchTerm}
                          onChange={(e) => {
                            const sanitizedValue = sanitizeInput(e.target.value, 'eventSearchTerm');
                            if (sanitizedValue.length <= MAX_LENGTHS.eventSearchTerm) {
                              setEventSearchTerm(sanitizedValue);
                            }
                          }}
                          maxLength={MAX_LENGTHS.eventSearchTerm}
                          className="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleSelectAllEvents}
                        className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 whitespace-nowrap"
                      >
                        <span className="pointer-events-none">Select All Events</span>
                      </button>
                    </div>
                    <div className="max-h-96 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-600">
                      {loadingEvents ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        </div>
                      ) : filteredEvents.length === 0 ? (
                        <div className="text-center py-8">
                          <CalendarDaysIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                            {eventSearchTerm ? "No events match your search" : "No events found"}
                          </h3>
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            {eventSearchTerm ? "Try adjusting your search terms" : "No events available for this studio"}
                          </p>
                        </div>
                      ) : (
                        filteredEvents.map((event) => (
                          <div
                            key={event.id}
                            className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-b-0 transition-colors duration-150"
                            onClick={() => handleEventSelect(event.id)}
                          >
                            <div className="flex items-center min-w-0">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {event.event_name}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                  {formatDate(event.from_date)}
                                  {event.from_date !== event.end_date && (
                                    <span className="text-gray-400"> - {formatDate(event.end_date)}</span>
                                  )} • {event.type}
                                  {event.start_time && event.end_time && (
                                    <span className="ml-2">
                                      {formatTime12Hour(event.start_time)} - {formatTime12Hour(event.end_time)}
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                            {selectedEvents.includes(event.id) && (
                              <CheckIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      onClick={handleEventsConfirm}
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                    >
                      <span className="pointer-events-none">Confirm</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleEventModalClose}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                    >
                      <span className="pointer-events-none">Cancel</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {templateToDelete && (
        <div className="fixed inset-0 z-20 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900 sm:mx-0 sm:h-10 sm:w-10">
                  <TrashIcon
                    className="h-6 w-6 text-red-600 dark:text-red-400"
                    aria-hidden="true"
                  />
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                    Delete Template
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Are you sure you want to delete "{templateToDelete.name}"?
                      This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDeleteTemplate}
                  className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                >
                  <span className="pointer-events-none">Delete</span>
                </button>
                <button
                  type="button"
                  onClick={() => setTemplateToDelete(null)}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                >
                  <span className="pointer-events-none">Cancel</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </PlanGatedPage>
  );
}
